// ============================================
// Parent Dashboard - Fixed version with proper API calls
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, Users, MessageSquare, Clock, Calendar, Shield, Settings,
  LogOut, Plus, Edit, Trash2, ChevronRight, Star, BookOpen,
  AlertTriangle, CheckCircle, BarChart3, Lock, Unlock
} from 'lucide-react';
import { useAuthStore, useChildrenStore } from '../store';
import type { Child } from '../types';

const API_BASE = 'https://equantu-kids-api-production.doziva228.workers.dev';

// ============================================
// Type Definitions
// ============================================

interface ParentSettings {
  id?: string;
  parent_id?: string;
  safety_level: number;
  max_session_duration_minutes: number;
  max_daily_session_minutes: number;
  max_daily_sessions: number;
  block_violence?: boolean;
  block_sexual?: boolean;
  block_terrorism?: boolean;
  block_drugs?: boolean;
  block_self_harm?: boolean;
  block_politics?: boolean;
  block_sectarian?: boolean;
  block_death_topics?: boolean;
  age_group_target?: string;
  preferred_persona?: string;
  allowed_personas?: string[];
  allowed_hours_start?: number;
  allowed_hours_end?: number;
  use_time_restrictions?: boolean;
  daily_summary_enabled?: boolean;
  weekly_report_enabled?: boolean;
  created_at?: number;
  updated_at?: number;
}

interface DashboardStats {
  totalChildren: number;
  today: { sessions: number; turns: number; tokens: number };
  thisWeek: { sessions: number; minutes: number };
  personaUsage: { persona_id: string; session_count: number; total_turns: number }[];
}

// ============================================
// Safety Level Slider Component
// ============================================

function SafetyLevelSlider({
  level,
  onChange,
  disabled = false
}: {
  level: number;
  onChange: (level: number) => void;
  disabled?: boolean;
}) {
  const levels = [
    { level: 1, name: '严格', color: 'bg-red-500', desc: '最高安全级别' },
    { level: 2, name: '很严格', color: 'bg-orange-500', desc: '高安全过滤' },
    { level: 3, name: '适中', color: 'bg-yellow-500', desc: '标准设置' },
    { level: 4, name: '宽松', color: 'bg-green-500', desc: '较低过滤' },
    { level: 5, name: '最宽松', color: 'bg-blue-500', desc: '最低限制' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">安全级别</span>
        <span className={`px-3 py-1 rounded-full text-sm font-bold text-white ${levels[level - 1]?.color || 'bg-gray-500'}`}>
          {levels[level - 1]?.name || '未知'}
        </span>
      </div>
      <input
        type="range"
        min="1"
        max="5"
        value={level}
        onChange={(e) => onChange(parseInt(e.target.value))}
        disabled={disabled}
        className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer"
      />
      <div className="flex justify-between text-xs text-gray-500">
        {levels.map((l) => (
          <span key={l.level} className={level === l.level ? 'font-bold text-gray-800' : ''}>
            {l.level}
          </span>
        ))}
      </div>
      <p className="text-sm text-gray-500">{levels[level - 1]?.desc || ''}</p>
    </div>
  );
}

// ============================================
// Child Safety Settings Modal Component
// ============================================

function ChildSafetySettings({
  child,
  settings,
  onSave,
  onClose
}: {
  child: Child;
  settings: ParentSettings;
  onSave: (data: ParentSettings) => Promise<void>;
  onClose: () => void;
}) {
  const [localSettings, setLocalSettings] = useState<ParentSettings>(settings);
  const [saving, setSaving] = useState(false);

  const personaOptions = [
    { id: 'noor', name: 'Noor老师', color: '#1565C0', desc: '温柔的古兰经老师' },
    { id: 'iman', name: 'Iman小伙伴', color: '#2E7D32', desc: '活泼的穆斯林小伙伴' },
    { id: 'hakeem', name: 'Hakeem爷爷', color: '#7B1FA2', desc: '智慧的穆斯林长者' },
  ];

  const ageGroupLabels: Record<string, string> = {
    '3-5': '幼儿 (3-5岁)',
    '6-8': '低年级 (6-8岁)',
    '9-12': '高年级 (9-12岁)',
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(localSettings);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">安全设置 - {child.name}</h2>
            <p className="text-sm text-gray-500">自定义 {child.name} 的对话安全边界</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[65vh] space-y-6">
          {/* 安全级别 */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold">安全级别</h3>
            </div>
            <SafetyLevelSlider
              level={localSettings.safety_level}
              onChange={(level) => setLocalSettings({ ...localSettings, safety_level: level })}
            />
          </div>

          {/* 允许的角色 */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              允许的AI角色
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {personaOptions.map((persona) => (
                <label
                  key={persona.id}
                  className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                    localSettings.allowed_personas?.includes(persona.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={localSettings.allowed_personas?.includes(persona.id) || false}
                    onChange={(e) => {
                      const personas = e.target.checked
                        ? [...(localSettings.allowed_personas || []), persona.id]
                        : (localSettings.allowed_personas || []).filter(p => p !== persona.id);
                      setLocalSettings({ ...localSettings, allowed_personas: personas });
                    }}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div>
                    <p className="font-medium text-sm">{persona.name}</p>
                    <p className="text-xs text-gray-500">{persona.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* 内容过滤 */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-600" />
              内容过滤
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={localSettings.block_violence || false}
                  onChange={(e) => setLocalSettings({ ...localSettings, block_violence: e.target.checked })}
                  className="w-4 h-4 text-red-600"
                />
                <span>暴力内容</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={localSettings.block_sexual || false}
                  onChange={(e) => setLocalSettings({ ...localSettings, block_sexual: e.target.checked })}
                  className="w-4 h-4 text-red-600"
                />
                <span>色情内容</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={localSettings.block_terrorism || false}
                  onChange={(e) => setLocalSettings({ ...localSettings, block_terrorism: e.target.checked })}
                  className="w-4 h-4 text-red-600"
                />
                <span>恐怖主义</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={localSettings.block_drugs || false}
                  onChange={(e) => setLocalSettings({ ...localSettings, block_drugs: e.target.checked })}
                  className="w-4 h-4 text-red-600"
                />
                <span>毒品相关</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={localSettings.block_self_harm || false}
                  onChange={(e) => setLocalSettings({ ...localSettings, block_self_harm: e.target.checked })}
                  className="w-4 h-4 text-red-600"
                />
                <span>自残/自杀</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={localSettings.block_politics || false}
                  onChange={(e) => setLocalSettings({ ...localSettings, block_politics: e.target.checked })}
                  className="w-4 h-4 text-yellow-600"
                />
                <span>政治话题</span>
              </label>
            </div>
          </div>

          {/* 时间控制 */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" />
              使用时间控制
            </h3>
            <label className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={localSettings.use_time_restrictions || false}
                onChange={(e) => setLocalSettings({ ...localSettings, use_time_restrictions: e.target.checked })}
                className="w-4 h-4 text-purple-600"
              />
              <span>限制使用时间</span>
            </label>
            
            {localSettings.use_time_restrictions && (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="block text-gray-600 mb-1">开始时间</label>
                  <input
                    type="time"
                    value={`${String(localSettings.allowed_hours_start || 8).padStart(2, '0')}:00`}
                    onChange={(e) => {
                      const [hours] = e.target.value.split(':').map(Number);
                      setLocalSettings({ ...localSettings, allowed_hours_start: hours });
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 mb-1">结束时间</label>
                  <input
                    type="time"
                    value={`${String(localSettings.allowed_hours_end || 21).padStart(2, '0')}:00`}
                    onChange={(e) => {
                      const [hours] = e.target.value.split(':').map(Number);
                      setLocalSettings({ ...localSettings, allowed_hours_end: hours });
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 时间限制 */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              使用时长限制
            </h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <label className="block text-gray-600 mb-1">单次对话</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={localSettings.max_session_duration_minutes || 30}
                    onChange={(e) => setLocalSettings({ ...localSettings, max_session_duration_minutes: parseInt(e.target.value) })}
                    className="w-20 px-3 py-2 border rounded-lg"
                  />
                  <span>分钟</span>
                </div>
              </div>
              <div>
                <label className="block text-gray-600 mb-1">每日上限</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={localSettings.max_daily_session_minutes || 120}
                    onChange={(e) => setLocalSettings({ ...localSettings, max_daily_session_minutes: parseInt(e.target.value) })}
                    className="w-20 px-3 py-2 border rounded-lg"
                  />
                  <span>分钟</span>
                </div>
              </div>
              <div>
                <label className="block text-gray-600 mb-1">每日次数</label>
                <input
                  type="number"
                  value={localSettings.max_daily_sessions || 10}
                  onChange={(e) => setLocalSettings({ ...localSettings, max_daily_sessions: parseInt(e.target.value) })}
                  className="w-20 px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* 通知设置 */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold mb-3">通知设置</h3>
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={localSettings.daily_summary_enabled || false}
                  onChange={(e) => setLocalSettings({ ...localSettings, daily_summary_enabled: e.target.checked })}
                  className="w-4 h-4 text-blue-600"
                />
                <span>每日学习总结</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={localSettings.weekly_report_enabled || false}
                  onChange={(e) => setLocalSettings({ ...localSettings, weekly_report_enabled: e.target.checked })}
                  className="w-4 h-4 text-blue-600"
                />
                <span>每周学习报告</span>
              </label>
            </div>
          </div>
        </div>

        <div className="p-6 border-t flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            {saving ? '保存中...' : '保存设置'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Main Parent Dashboard Component
// ============================================

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { children, fetchChildren } = useChildrenStore();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [settings, setSettings] = useState<ParentSettings | null>(null);
  const [settingsChild, setSettingsChild] = useState<Child | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const parentToken = typeof window !== 'undefined' ? localStorage.getItem('parent-token') : null;

  // Safe fetch function
  const safeFetch = useCallback(async (url: string, options?: RequestInit) => {
    if (!parentToken) {
      throw new Error('No authentication token');
    }
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${parentToken}`,
        ...options?.headers,
      },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json();
  }, [parentToken]);

  useEffect(() => {
    if (!parentToken) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [parentToken]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      // 并行获取数据
      const [statsRes, settingsRes] = await Promise.all([
        safeFetch(`${API_BASE}/api/parent/dashboard`).catch(e => ({ children: [], todayStats: {}, personaStats: [] })),
        safeFetch(`${API_BASE}/api/parent/settings`).catch(e => ({ settings: null })),
      ]);

      // 处理 stats 数据
      if (statsRes.children) {
        setStats({
          totalChildren: statsRes.children.length || 0,
          today: statsRes.todayStats || { sessions: 0, turns: 0, tokens: 0 },
          thisWeek: statsRes.thisWeek || { sessions: 0, minutes: 0 },
          personaUsage: statsRes.personaStats || [],
        });
      }

      // 处理 settings 数据
      if (settingsRes.settings) {
        setSettings(settingsRes.settings);
      } else {
        // 如果没有设置，使用默认值
        setSettings({
          safety_level: 3,
          max_session_duration_minutes: 30,
          max_daily_session_minutes: 120,
          max_daily_sessions: 10,
          allowed_personas: ['noor', 'iman', 'hakeem'],
          allowed_hours_start: 8,
          allowed_hours_end: 21,
          use_time_restrictions: false,
          daily_summary_enabled: true,
          weekly_report_enabled: true,
        });
      }

      // 获取孩子列表
      await fetchChildren();
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('加载数据失败，请刷新页面重试');
    }
    setLoading(false);
  };

  const handleSaveSettings = async (newSettings: ParentSettings) => {
    try {
      const res = await safeFetch(`${API_BASE}/api/parent/settings`, {
        method: 'PUT',
        body: JSON.stringify(newSettings),
      });

      if (res.settings) {
        setSettings(res.settings);
        setMessage('设置已保存！');
        setSettingsChild(null);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError('保存失败：无法更新设置');
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('保存失败: ' + (err as Error).message);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const ageGroupLabels: Record<string, string> = {
    '3-5': '3-5岁',
    '6-8': '6-8岁',
    '9-12': '9-12岁',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-noor-500 to-iman-500 bg-clip-text text-transparent">
                Equantu Kids
              </h1>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">家长控制面板</span>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/chat')}
                className="flex items-center gap-2 px-4 py-2 bg-noor-500 text-white rounded-xl hover:bg-noor-600 transition-colors"
              >
                <MessageSquare className="w-5 h-5" />
                开始对话
              </button>
              <div className="flex items-center gap-2 text-gray-600">
                <User className="w-5 h-5" />
                <span>{user?.name || user?.email || '家长'}</span>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')}>✕</button>
          </div>
        )}

        {message && (
          <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg flex items-center justify-between">
            <span>{message}</span>
            <button onClick={() => setMessage('')}>✕</button>
          </div>
        )}

        {/* Safety Banner */}
        {settings && (
          <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-6 mb-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold mb-2">🛡️ 当前安全级别</h2>
                <p className="opacity-90">
                  {settings.safety_level === 1 && '严格 - 最高安全保护'}
                  {settings.safety_level === 2 && '很严格 - 高安全过滤'}
                  {settings.safety_level === 3 && '适中 - 标准安全设置'}
                  {settings.safety_level === 4 && '宽松 - 较低限制'}
                  {settings.safety_level === 5 && '最宽松 - 最小限制'}
                </p>
              </div>
              <button
                onClick={() => {
                  // 使用第一个孩子的设置
                  if (children.length > 0) {
                    setSettingsChild(children[0]);
                  }
                }}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                ⚙️ 安全设置
              </button>
            </div>
          </div>
        )}

        {/* Stats Overview */}
        {stats && (
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">使用统计</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-5">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-noor-400 to-noor-500">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">孩子档案</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.totalChildren}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-5">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-iman-400 to-iman-500">
                    <MessageSquare className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">今日对话</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.today?.sessions || 0}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-5">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-hakeem-400 to-hakeem-500">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">本周时长</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.thisWeek?.minutes || 0}分钟</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-5">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">对话轮数</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.today?.turns || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Children Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800">孩子档案</h2>
            <button
              onClick={() => navigate('/register-child')}
              className="flex items-center gap-2 px-4 py-2 bg-noor-500 text-white rounded-xl hover:bg-noor-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              添加孩子
            </button>
          </div>

          {children.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-md p-12 text-center">
              <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                还没有添加孩子
              </h3>
              <p className="text-gray-500 mb-6">
                添加孩子档案后，即可开始AI陪伴学习之旅
              </p>
              <button
                onClick={() => navigate('/register-child')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-noor-500 text-white rounded-xl hover:bg-noor-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                添加第一个孩子
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {children.map((child) => (
                <div key={child.id} className="bg-white rounded-2xl shadow-md p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-noor-400 to-iman-400 flex items-center justify-center text-white text-2xl font-bold">
                        {child.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800">{child.name}</h3>
                        {child.nickname && (
                          <p className="text-gray-500 text-sm">昵称: {child.nickname}</p>
                        )}
                        <span className="inline-block mt-1 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-600">
                          {child.ageGroup ? (ageGroupLabels[child.ageGroup] || child.ageGroup) : (child.age_group ? (ageGroupLabels[child.age_group] || child.age_group) : '未设置')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="border-t pt-4 mt-4 grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setSettingsChild(child)}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors text-blue-700"
                    >
                      <Shield className="w-5 h-5" />
                      安全设置
                    </button>
                    <button
                      onClick={() => navigate(`/history/${child.id}`)}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors text-gray-700"
                    >
                      <Clock className="w-5 h-5" />
                      对话历史
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Safety Logs Section */}
        <section className="mt-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">安全日志</h2>
            <p className="text-gray-500 text-sm mb-4">查看被拦截的内容和时间限制记录</p>
            
            <div className="text-center py-8 text-gray-400">
              <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>暂无安全拦截记录</p>
              <p className="text-sm">当孩子尝试访问被限制的内容时会显示在这里</p>
            </div>
          </div>
        </section>
      </main>

      {/* Settings Modal */}
      {settingsChild && settings && (
        <ChildSafetySettings
          child={settingsChild}
          settings={settings}
          onSave={handleSaveSettings}
          onClose={() => setSettingsChild(null)}
        />
      )}
    </div>
  );
}
